import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, PageHeader, Modal, Button, Field, Input, Select, Textarea } from "@/components/ui-kit";
import FilePreview from "@/components/FilePreview";
import { Plus, Trash2, Eye, FileText, Upload, Check, X as XIcon, ClipboardList } from "lucide-react";
import { formatDate, todayISO } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Iter v34bb — Pengajuan Fasilitas / Alat / Keperluan Karyawan.
 * - Karyawan: buat pengajuan → email otomatis ke admin.
 * - Admin: approve/reject di halaman ini. Karyawan lihat statusnya.
 */
const blankForm = {
  category_id: "", category_name: "", title: "", description: "",
  urgency: "normal", quantity: 1, unit: "pcs", attachments: [],
};

export default function EmployeeRequests() {
  const { hasPerm, user } = useAuth();
  const canApprove = hasPerm("employee_request.approve");
  const canAdmin = hasPerm("employee_request.admin");
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showMine, setShowMine] = useState(!canApprove); // Karyawan default 'mine'
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [decideRow, setDecideRow] = useState(null);
  const [decideNote, setDecideNote] = useState("");
  const [previewFile, setPreviewFile] = useState(null);

  const load = () => {
    api.get("/employee-requests", { params: showMine ? { mine: true } : {} }).then((r) => setRows(r.data || []));
    api.get("/employee-requests/categories").then((r) => setCategories(r.data || []));
  };
  useEffect(() => { load(); }, [showMine]);

  const openCreate = () => { setForm(blankForm); setOpen(true); };

  const uploadAttachment = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file); fd.append("kind", "employee_request");
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ id: r.data.id, path: r.data.path, filename: r.data.filename, content_type: r.data.content_type });
      }
      setForm((f) => ({ ...f, attachments: [...(f.attachments || []), ...uploaded] }));
      toast.success(files.length > 1 ? `${files.length} lampiran terunggah` : "Lampiran terunggah");
    } catch { toast.error("Gagal upload"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    if (!form.category_name.trim()) { toast.error("Kategori wajib"); return; }
    if (!form.title.trim()) { toast.error("Judul wajib"); return; }
    setSaving(true);
    try {
      const r = await api.post("/employee-requests", form);
      const sent = r.data?.email_notification?.sent || 0;
      toast.success(`Pengajuan tersimpan${sent > 0 ? ` — email terkirim ke ${sent} penerima` : " (email penerima belum diatur)"}`);
      setOpen(false); setForm(blankForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  };

  const decide = async (status) => {
    if (!decideRow) return;
    try {
      await api.post(`/employee-requests/${decideRow.id}/decision`, { status, admin_note: decideNote });
      toast.success(status === "approved" ? "Disetujui" : "Ditolak");
      setDecideRow(null); setDecideNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const del = async (r) => {
    if (!window.confirm("Hapus pengajuan ini?")) return;
    try { await api.delete(`/employee-requests/${r.id}`); load(); toast.success("Dihapus"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const statusPill = (s) => {
    const map = {
      pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-800",
    };
    const label = { pending: "Pending", approved: "Disetujui", rejected: "Ditolak" }[s] || s;
    return <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider ${map[s] || "bg-stone-100"}`}>{label}</span>;
  };
  const urgencyPill = (u) => {
    const map = { low: "text-stone-500", normal: "text-blue-700", high: "text-amber-700", urgent: "text-red-700 font-bold" };
    const label = { low: "Rendah", normal: "Normal", high: "Tinggi", urgent: "URGENT" }[u] || u;
    return <span className={`text-[10px] uppercase tracking-wider ${map[u] || ""}`}>{label}</span>;
  };

  return (
    <div className="min-h-full bg-paper p-4 md:p-6 lg:p-8">
      <div className="max-w-[1500px] mx-auto space-y-6">
        <PageHeader
          overline="Pengajuan Karyawan"
          title="Pengajuan Fasilitas / Alat / Keperluan"
          description="Ajukan permintaan fasilitas, alat kerja, konsumsi, atau perbaikan. Email otomatis dikirim ke penerima yang diatur di Settings."
          icon={ClipboardList}
          action={
            <div className="flex gap-2">
              {canApprove && (
                <Button variant="ghost" onClick={() => setShowMine((s) => !s)} data-testid="req-toggle-mine">
                  {showMine ? "Tampilkan Semua" : "Pengajuan Saya"}
                </Button>
              )}
              {hasPerm("employee_request.create") && (
                <Button onClick={openCreate} data-testid="req-open-create">
                  <Plus className="h-3.5 w-3.5" /> Buat Pengajuan
                </Button>
              )}
            </div>
          }
        />

        <Card className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="req-table">
              <thead className="bg-stone-50">
                <tr className="text-left text-[11px] uppercase tracking-widest text-stone-500 border-b">
                  <th className="py-3 px-3">Tanggal</th>
                  <th className="py-3 px-3">Pengaju</th>
                  <th className="py-3 px-3">Kategori</th>
                  <th className="py-3 px-3">Judul</th>
                  <th className="py-3 px-3 text-right">Qty</th>
                  <th className="py-3 px-3 text-center">Urgensi</th>
                  <th className="py-3 px-3 text-center">Status</th>
                  <th className="py-3 px-3">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-10 text-stone-400 text-xs">Belum ada pengajuan.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`req-row-${r.id}`}>
                    <td className="py-2 px-3 font-mono text-xs">{formatDate(r.created_at)}</td>
                    <td className="py-2 px-3">{r.requester_name}</td>
                    <td className="py-2 px-3 text-stone-600">{r.category_name || "-"}</td>
                    <td className="py-2 px-3">
                      <div className="font-medium">{r.title}</div>
                      <div className="text-[11px] text-stone-500 line-clamp-2">{r.description}</div>
                      {(r.attachments || []).length > 0 && (
                        <div className="mt-1 flex gap-1 flex-wrap">
                          {(r.attachments || []).map((a, ai) => (
                            <button key={a.id || ai} onClick={() => setPreviewFile({ id: a.id, name: a.filename, ct: a.content_type })}
                              className="inline-flex items-center gap-1 text-[10px] text-blue-700 border border-stone-200 px-1 py-0.5 rounded hover:bg-blue-50">
                              <Eye className="h-3 w-3" /> {ai + 1}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right font-mono">{r.quantity} {r.unit}</td>
                    <td className="py-2 px-3 text-center">{urgencyPill(r.urgency)}</td>
                    <td className="py-2 px-3 text-center">
                      {statusPill(r.status)}
                      {r.reviewed_by && r.status !== "pending" && (
                        <div className="text-[9px] text-stone-500 mt-0.5">oleh {r.reviewed_by}</div>
                      )}
                      {r.admin_note && (
                        <div className="text-[10px] text-stone-600 italic mt-0.5">&quot;{r.admin_note}&quot;</div>
                      )}
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      {canApprove && r.status === "pending" && (
                        <button onClick={() => { setDecideRow(r); setDecideNote(""); }}
                          className="text-emerald-700 hover:bg-emerald-50 px-2 py-1 text-[10px] uppercase tracking-wider rounded"
                          data-testid={`req-review-${r.id}`}>
                          Review
                        </button>
                      )}
                      {(canAdmin || (r.status === "pending" && r.requester_email === user?.email)) && (
                        <button onClick={() => del(r)} className="text-stone-400 hover:text-red-700 px-2" data-testid={`req-del-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Create Modal */}
      <Modal open={open} onClose={() => setOpen(false)} title="Buat Pengajuan Baru" size="lg">
        <div className="space-y-4" data-testid="req-modal">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Kategori">
              <Select
                value={form.category_id}
                onChange={(e) => {
                  const c = categories.find((cc) => cc.id === e.target.value);
                  setForm({ ...form, category_id: e.target.value, category_name: c?.name || "" });
                }}
                data-testid="req-category"
              >
                <option value="">— Pilih Kategori —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
              {categories.length === 0 && <p className="text-[11px] text-stone-500 mt-1">Belum ada kategori — Admin buat dulu di Database → Kategori Pengajuan.</p>}
            </Field>
            <Field label="Urgensi">
              <Select value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })} data-testid="req-urgency">
                <option value="low">Rendah</option>
                <option value="normal">Normal</option>
                <option value="high">Tinggi</option>
                <option value="urgent">URGENT</option>
              </Select>
            </Field>
          </div>
          <Field label="Judul Pengajuan">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="mis: Bor listrik untuk workshop" data-testid="req-title" />
          </Field>
          <Field label="Deskripsi / Alasan">
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Jelaskan kebutuhan Anda" data-testid="req-description" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Jumlah">
              <Input type="number" step="1" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) || 1 })} data-testid="req-qty" />
            </Field>
            <Field label="Satuan">
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="pcs, unit, set, dsb" />
            </Field>
          </div>
          <Field label="Lampiran (foto/PDF)">
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 rounded cursor-pointer hover:bg-stone-50 text-sm w-fit">
              <Upload className="h-4 w-4" />{uploading ? "Mengunggah…" : ((form.attachments || []).length ? "Tambah lampiran" : "Pilih file (multi)")}
              <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf" onChange={uploadAttachment} className="hidden" data-testid="req-attach-input" />
            </label>
            {(form.attachments || []).length > 0 && (
              <div className="mt-2 space-y-1 border border-stone-200 rounded p-2 bg-stone-50/50">
                {(form.attachments || []).map((pf, idx) => (
                  <div key={pf.id || idx} className="flex items-center gap-2 text-xs">
                    <FileText className="h-3.5 w-3.5 text-stone-500 shrink-0" />
                    <span className="text-emerald-800 truncate flex-1">{pf.filename}</span>
                    <button type="button" onClick={() => setForm((f) => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }))} className="text-stone-500 hover:text-red-700">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-stone-100">
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submit} disabled={saving} data-testid="req-submit-btn">
              {saving ? "Mengirim..." : "Kirim Pengajuan"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Decision Modal */}
      <Modal open={!!decideRow} onClose={() => setDecideRow(null)} title="Review Pengajuan" size="md">
        {decideRow && (
          <div className="space-y-4" data-testid="req-decide-modal">
            <div className="bg-stone-50 border border-stone-200 p-3 text-sm rounded">
              <div className="font-medium">{decideRow.title}</div>
              <div className="text-xs text-stone-500 mt-1">
                {decideRow.requester_name} · {decideRow.category_name} · {decideRow.quantity} {decideRow.unit}
              </div>
              <div className="text-sm text-stone-700 mt-2 whitespace-pre-wrap">{decideRow.description}</div>
            </div>
            <Field label="Catatan Admin (opsional)">
              <Textarea rows={2} value={decideNote} onChange={(e) => setDecideNote(e.target.value)} placeholder="mis: Silakan ke gudang ambil unit yg tersedia" data-testid="req-decide-note" />
            </Field>
            <div className="flex justify-end gap-2 pt-3 border-t border-stone-100">
              <Button variant="ghost" onClick={() => setDecideRow(null)}>Batal</Button>
              <Button variant="secondary" onClick={() => decide("rejected")} data-testid="req-reject-btn">
                <XIcon className="h-4 w-4" /> Tolak
              </Button>
              <Button onClick={() => decide("approved")} data-testid="req-approve-btn">
                <Check className="h-4 w-4" /> Setujui
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <FilePreview open={!!previewFile} onClose={() => setPreviewFile(null)} fileId={previewFile?.id} filename={previewFile?.name} contentType={previewFile?.ct} />
    </div>
  );
}
